import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, User, Mail, Phone, Trash2, Edit2, Star, Briefcase, Search, Globe, Copy, Check, DollarSign, Award, TrendingUp, X, Target, BookOpen, Eye, Volume2, FileText, Upload, Download, AlertTriangle, Info, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActors() {
  const navigate = useNavigate();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [warningsActor, setWarningsActor] = useState(null);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [newCoachingNote, setNewCoachingNote] = useState({ category: '', content: '' });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});  // Unread form submission counts
  
  // KB Chunks selection state
  const [availableKBChunks, setAvailableKBChunks] = useState([]);
  const [loadingKBChunks, setLoadingKBChunks] = useState(false);
  const [showKBChunkSelector, setShowKBChunkSelector] = useState(false);
  const [editingNoteData, setEditingNoteData] = useState({ category: '', content: '' });
  
  // ElevenLabs voices state
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [groupedVoices, setGroupedVoices] = useState({ cloned: [], professional: [], premade: [] });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    about: "",
    tagline: "",  // Short professional tagline
    work_profile: "",
    background: "",
    experience_level: "",
    default_llm_model: "", // Required field
    fallback_models: [], // Ordered list of fallback models (priority order)
    system_prompt: "", // System prompt for chat
    
    // Guard Rail Settings
    guard_rail_enabled: true,
    guard_rail_strictness: "moderate",
    guard_rail_model: "google/gemini-2.0-flash-exp:free",
    custom_refusal_message: "",
    
    // Voice Configuration for Public Profile Calls
    voice_mode: "openai", // "openai" or "elevenlabs"
    openai_voice_preference: "alloy", // OpenAI voice selection
    voice_id: "", // ElevenLabs voice ID
    voice_name: "", // ElevenLabs voice name
    voice_speed: 1.0,
    voice_stability: 0.5,
    voice_similarity: 0.75,
    voice_style: 0.0,
    voice_speaker_boost: true,
    
    // Knowledge Base Configuration
    kb_similarity_threshold: 0.7, // Semantic search similarity threshold (0-1, 0 = no threshold)
    voice_ai_kb_item_limit: 100, // Max KB items to load at session start
    selected_kb_chunk_ids: [], // Manually selected KB chunk IDs
    
    // Voice AI Profile Configuration
    voice_profile_fields: ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
    
    // Voice AI Feedback Configuration
    voice_include_feedback: true,
    voice_feedback_types: ["user", "admin"],
    
    // Tag-Based Function Calling (Fallback)
    enable_tag_functions: false,
    
    // Voice AI Conversation Configuration
    filter_used_kb_items: false, // Filter out already used KB items
    chat_history_limit: 20, // Number of messages in chat history
    
    // Voice AI VAD Configuration
    vad_sensitivity_threshold: 0.5, // VAD sensitivity (0.0-1.0)
    vad_silence_duration_ms: 700, // Silence duration in milliseconds
    enable_true_barge_in: false, // Enable interruption while AI is speaking
    barge_in_message: "Yes, please go ahead.", // Message AI says when interrupted
    
    ip_rate_limit_enabled: false, // IP rate limiting toggle
    responsibilities: [],
    skills: [],
    social_media: [],
    projects: [],
    customer_reviews: [],
    is_public: false,
    
    // Pricing
    hourly_rate: "",
    packages: [],
    
    // Conversion Elements
    verified_badges: [],
    availability_status: "Available",
    jobs_completed: 0,
    success_rate: 100,
    response_time: "Within 24 hours",
    video_url: "",
    case_studies: [],
    industries_served: [],
    languages: [],
    
    // Trust Indicators
    revenue_generated: "",
    time_saved_stats: "",
    credentials: []
  });

  useEffect(() => {
    fetchActors();
    fetchOpenRouterModels();
    fetchUnreadCounts();
  }, []);

  const fetchActors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Normalize actors to ensure new fields have defaults
      const normalizedActors = response.data.map(actor => ({
        ...actor,
        voice_ai_kb_item_limit: actor.voice_ai_kb_item_limit || 100,
        selected_kb_chunk_ids: actor.selected_kb_chunk_ids || [],
        voice_profile_fields: actor.voice_profile_fields || ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
        voice_include_feedback: actor.voice_include_feedback !== undefined ? actor.voice_include_feedback : true,
        voice_feedback_types: actor.voice_feedback_types || ["user", "admin"]
      }));
      
      setActors(normalizedActors);
    } catch (error) {
      toast.error("Failed to fetch AI actors");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/form-submissions/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCounts(response.data);
    } catch (error) {
      console.error("Failed to fetch unread counts:", error);
    }
  };

  const fetchOpenRouterModels = async () => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API}/openrouter/models`);
      setOpenrouterModels(response.data.models || []);
    } catch (error) {
      console.error("Failed to fetch OpenRouter models:", error);
      // Don't show error toast, just log it
    } finally {
      setLoadingModels(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Fetch available KB chunks for actor
  const fetchKBChunks = async (actorId) => {
    setLoadingKBChunks(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}/kb-chunks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableKBChunks(response.data.kb_chunks || []);
      console.log(`Loaded ${response.data.total_count} KB chunks for actor`);
    } catch (error) {
      console.error("Failed to fetch KB chunks:", error);
      toast.error("Failed to load Knowledge Base chunks");
    } finally {
      setLoadingKBChunks(false);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    const formData = new FormData();
    formData.append('file', imageFile);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/ai/actors/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.image_path;
    } catch (error) {
      toast.error("Failed to upload image");
      throw error;
    }
  };

  const fetchVoices = async () => {
    setLoadingVoices(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/elevenlabs/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const voicesList = response.data.voices || [];
      setVoices(voicesList);
      
      // Group voices by category
      const grouped = {
        cloned: voicesList.filter(v => v.category === "cloned"),
        professional: voicesList.filter(v => v.category === "professional"),
        premade: voicesList.filter(v => v.category === "premade" || !v.category)
      };
      setGroupedVoices(grouped);
    } catch (error) {
      console.error("Error fetching voices:", error);
      toast.error("Failed to load voices");
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.default_llm_model) {
      toast.error("Please select a default LLM model");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      
      // Upload image if present
      let imagePath = formData.profile_image;
      if (imageFile) {
        imagePath = await uploadImage();
      }
      
      // Process credentials - convert string to array if needed
      let processedCredentials = formData.credentials;
      if (typeof formData.credentials === 'string') {
        processedCredentials = formData.credentials
          .split(",")
          .map(s => s.trim())
          .filter(s => s);
      }
      
      // Sanitize numeric fields - convert empty strings to null for optional float/int fields
      const sanitizeNumericField = (value) => {
        if (value === "" || value === null || value === undefined) {
          return undefined; // Return undefined to exclude field from JSON
        }
        // Convert string numbers to actual numbers
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      };
      
      console.log("🔍 DEBUG: Original formData.hourly_rate:", formData.hourly_rate, typeof formData.hourly_rate);
      
      // Build actorData with sanitized numeric fields
      const sanitizedNumericFields = {
        hourly_rate: sanitizeNumericField(formData.hourly_rate),
        voice_speed: sanitizeNumericField(formData.voice_speed),
        voice_stability: sanitizeNumericField(formData.voice_stability),
        voice_similarity: sanitizeNumericField(formData.voice_similarity),
        voice_style: sanitizeNumericField(formData.voice_style),
        vad_sensitivity_threshold: sanitizeNumericField(formData.vad_sensitivity_threshold),
        vad_silence_duration_ms: sanitizeNumericField(formData.vad_silence_duration_ms),
        chat_history_limit: sanitizeNumericField(formData.chat_history_limit),
        voice_ai_kb_item_limit: sanitizeNumericField(formData.voice_ai_kb_item_limit),
        kb_similarity_threshold: sanitizeNumericField(formData.kb_similarity_threshold)
      };
      
      // Remove undefined fields
      Object.keys(sanitizedNumericFields).forEach(key => {
        if (sanitizedNumericFields[key] === undefined) {
          delete sanitizedNumericFields[key];
        }
      });
      
      const actorData = {
        ...formData,
        profile_image: imagePath,
        credentials: processedCredentials,
        ...sanitizedNumericFields // Override with sanitized fields
      };
      
      console.log("🔍 DEBUG: Final actorData.hourly_rate:", actorData.hourly_rate, typeof actorData.hourly_rate);
      console.log("🔍 DEBUG: actorData keys:", Object.keys(actorData).filter(k => k.includes('hourly') || k.includes('voice_speed')));
      
      if (editingActor) {
        await axios.put(`${API}/ai/actors/${editingActor.id}`, actorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("AI Actor updated successfully");
      } else {
        const createRes = await axios.post(`${API}/ai/actors`, actorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const status = createRes.data?.setup_status || { warnings: [], optional: [] };
        const wCount = status.warnings?.length || 0;
        const oCount = status.optional?.length || 0;
        if (wCount > 0 || oCount > 0) {
          const parts = [];
          if (wCount > 0) parts.push(`${wCount} recommended`);
          if (oCount > 0) parts.push(`${oCount} optional`);
          toast.warning(`AI Actor created. ${parts.join(" + ")} setup item(s) pending — click the ⚠/ℹ icon on its card to review.`);
        } else {
          toast.success("AI Actor created successfully");
        }
      }
      
      fetchActors();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const fieldLabels = {
        name: "Name",
        email: "Email",
        default_llm_model: "Default LLM model",
      };
      let message = editingActor ? "Failed to update actor" : "Failed to create actor";
      if (Array.isArray(detail)) {
        const fields = detail
          .map((d) => {
            const key = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
            return fieldLabels[key] || key;
          })
          .filter(Boolean);
        if (fields.length > 0) {
          message = `Couldn't ${editingActor ? "update" : "create"} actor — missing or invalid: ${fields.join(", ")}`;
        }
      } else if (typeof detail === "string") {
        message = detail;
      }
      toast.error(message);
      console.error(error);
    }
  };

  const fetchCoachingNotes = async (actorId) => {
    try {
      setLoadingNotes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-actors/${actorId}/coaching-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachingNotes(response.data.coaching_notes || []);
    } catch (error) {
      console.error("Failed to fetch coaching notes", error);
      setCoachingNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const deleteCoachingNote = async (actorId, noteId) => {
    if (!window.confirm("Are you sure you want to delete this coaching note?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/ai-actors/${actorId}/coaching-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coaching note deleted");
      fetchCoachingNotes(actorId); // Refresh notes
    } catch (error) {
      toast.error("Failed to delete coaching note");
      console.error(error);
    }
  };

  const addCoachingNote = async () => {
    if (!newCoachingNote.category || !newCoachingNote.content) {
      toast.error("Please fill in both category and content");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-actors/${editingActor.id}/coaching-notes`,
        {
          category: newCoachingNote.category,
          content: newCoachingNote.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note added successfully");
      setNewCoachingNote({ category: '', content: '' });
      fetchCoachingNotes(editingActor.id); // Refresh notes
    } catch (error) {
      toast.error("Failed to add coaching note");
      console.error(error);
    }
  };

  const saveCoachingNote = async (actorId, noteId) => {
    if (!editingNoteData.category || !editingNoteData.content) {
      toast.error("Please fill in both category and content");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/ai-actors/${actorId}/coaching-notes/${noteId}`,
        {
          category: editingNoteData.category,
          content: editingNoteData.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note updated successfully");
      setEditingNoteId(null);
      setEditingNoteData({ category: '', content: '' });
      fetchCoachingNotes(actorId); // Refresh notes
    } catch (error) {
      toast.error("Failed to update coaching note");
      console.error(error);
    }
  };

  const handleEdit = (actor) => {
    navigate(`/ai/actors/edit/${actor.id}`);
  };

  const handleDelete = async (actorId) => {
    if (!window.confirm("Are you sure you want to delete this AI Actor?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("AI Actor deleted successfully");
      fetchActors();
    } catch (error) {
      toast.error("Failed to delete actor");
      console.error(error);
    }
  };

  const resetForm = () => {
    setEditingActor(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      about: "",
      tagline: "",
      work_profile: "",
      background: "",
      experience_level: "",
      default_llm_model: "",
      system_prompt: "",
      ip_rate_limit_enabled: false,
      responsibilities: [],
      skills: [],
      social_media: [],
      projects: [],
      customer_reviews: [],
      is_public: false,
      
      // Pricing
      hourly_rate: "",
      packages: [],
      
      // Conversion Elements
      verified_badges: [],
      availability_status: "Available",
      jobs_completed: 0,
      success_rate: 100,
      response_time: "Within 24 hours",
      video_url: "",
      case_studies: [],
      industries_served: [],
      languages: [],
      
      // Trust Indicators
      revenue_generated: "",
      time_saved_stats: "",
      credentials: []
    });
  };

  const createSlug = (name) => {
    return name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')  // Strip diacritics (accents)
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[-\s]+/g, '-')
      .trim();
  };

  const copyPublicUrl = (actor) => {
    const slug = createSlug(actor.name);
    const publicUrl = `${window.location.origin}/ai-agent/${slug}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopiedId(actor.id);
      toast.success("Public URL copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const filteredActors = actors.filter(actor =>
    actor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    actor.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const averageRating = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">AI Actors</h1>
            <p className="text-slate-600">Manage your digital human profiles</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => document.getElementById('import-actor-input')?.click()}
              data-testid="import-actor-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Actor
            </Button>
            <input
              id="import-actor-input"
              type="file"
              accept=".json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API}/ai/actors/import`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData,
                  });
                  const data = await res.json();
                  if (data.success) {
                    toast.success(data.message);
                    if (data.warnings?.length > 0) toast.warning(data.warnings[0]);
                    fetchActors();
                  } else {
                    toast.error(data.detail || 'Import failed');
                  }
                } catch (err) {
                  toast.error('Import failed: ' + err.message);
                }
              }}
            />
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => navigate('/ai/actors/create')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create AI Actor
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/ai/actors/readiness-settings')}
              data-testid="readiness-settings-btn"
              title="Readiness score settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search actors by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Actors Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-slate-600">Loading AI actors...</p>
          </div>
        ) : filteredActors.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="pt-20 pb-20">
              <div className="text-center">
                <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No AI Actors Found</h3>
                <p className="text-slate-500 mb-6">
                  {searchQuery ? "Try a different search term" : "Create your first AI actor to get started"}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredActors.map((actor) => (
              <Card key={actor.id} className="border-slate-200 hover:shadow-md transition-shadow flex flex-col relative">
                {/* Setup Warning Icon - functional config (🟡) */}
                {actor.setup_status?.warnings?.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setWarningsActor(actor)}
                    data-testid={`actor-warnings-btn-${actor.id}`}
                    title={`${actor.setup_status.warnings.length} recommended setup item(s)`}
                    className="absolute top-2 right-[4.5rem] text-amber-500 hover:text-amber-600 hover:bg-amber-50 h-8 px-1.5 p-0 flex items-center gap-0.5"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-semibold">{actor.setup_status.warnings.length}</span>
                  </Button>
                )}
                {/* Setup Optional Icon - good-to-have (🔵) */}
                {actor.setup_status?.optional?.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setWarningsActor(actor)}
                    data-testid={`actor-optional-btn-${actor.id}`}
                    title={`${actor.setup_status.optional.length} optional setup item(s)`}
                    className="absolute top-2 right-10 text-blue-500 hover:text-blue-600 hover:bg-blue-50 h-8 px-1.5 p-0 flex items-center gap-0.5"
                  >
                    <Info className="w-4 h-4" />
                    <span className="text-xs font-semibold">{actor.setup_status.optional.length}</span>
                  </Button>
                )}
                {/* Delete Button - Top Right */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(actor.id)}
                  className="absolute top-2 right-2 text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    {actor.profile_image ? (
                      <img
                        src={`${API}/ai/actors/images/${actor.profile_image}`}
                        alt={actor.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-indigo-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 pr-8">
                      <CardTitle className="text-lg truncate">{actor.name}</CardTitle>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {actor.experience_level && (
                          <Badge variant="secondary" className="text-xs">
                            {actor.experience_level}
                          </Badge>
                        )}
                        {actor.readiness && (
                          <Badge 
                            variant="outline" 
                            data-testid={`actor-readiness-${actor.id}`}
                            title={`Setup ${actor.readiness.setup_score}% · Trained ${actor.readiness.training_score}% — ${actor.readiness.tier_label}`}
                            className={`text-xs
                              ${actor.readiness.tier_color === 'green' ? 'bg-green-50 text-green-700 border-green-300' : ''}
                              ${actor.readiness.tier_color === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-300' : ''}
                              ${actor.readiness.tier_color === 'orange' ? 'bg-orange-50 text-orange-700 border-orange-300' : ''}
                              ${actor.readiness.tier_color === 'red' ? 'bg-red-50 text-red-700 border-red-300' : ''}
                            `}
                          >
                            <Target className="w-3 h-3 mr-1" />
                            {actor.readiness.readiness}/100
                          </Badge>
                        )}
                        {actor.voice_name && (
                          <Badge 
                            variant="outline" 
                            className="bg-purple-50 text-purple-700 border-purple-300 text-xs"
                          >
                            <Volume2 className="w-3 h-3 mr-1" />
                            {actor.voice_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="space-y-2.5 flex-1">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{actor.email}</span>
                    </div>
                    
                    {actor.phone && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{actor.phone}</span>
                      </div>
                    )}

                    {actor.customer_reviews && actor.customer_reviews.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-semibold">{averageRating(actor.customer_reviews)}</span>
                        <span className="text-slate-500">({actor.customer_reviews.length} reviews)</span>
                      </div>
                    )}

                    {actor.about && (
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {actor.about}
                      </p>
                    )}

                    {actor.skills && actor.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {actor.skills.slice(0, 3).map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {actor.skills.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{actor.skills.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}

                    {actor.projects && actor.projects.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Briefcase className="w-4 h-4" />
                        <span>{actor.projects.length} projects</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons - Fixed at bottom */}
                  <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
                    {/* Manage Button */}
                    <Button
                      size="sm"
                      onClick={() => navigate(`/ai/actors/manage/${actor.id}`)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Manage
                    </Button>
                    
                    {/* Export Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      data-testid={`export-actor-${actor.id}`}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API}/ai/actors/${actor.id}/export`, {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                          });
                          if (!res.ok) throw new Error('Export failed');
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = res.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `${actor.name}_export.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success(`Exported ${actor.name}`);
                        } catch (err) {
                          toast.error('Export failed: ' + err.message);
                        }
                      }}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Export
                    </Button>
                    
                    {/* View, Edit, and Delete buttons hidden as per requirement */}
                    {/* <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/ai/actors/view/${actor.id}`)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(actor)}
                        className="flex-1"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(actor.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div> */}

                    {/* Public Profile URL and Form Responses */}
                    {actor.is_public && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyPublicUrl(actor)}
                          className="w-full text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          {copiedId === actor.id ? (
                            <>
                              <Check className="w-3 h-3 mr-2" />
                              Public Link Copied!
                            </>
                          ) : (
                            <>
                              <Globe className="w-3 h-3 mr-2" />
                              <Copy className="w-3 h-3 mr-2" />
                              Copy Public Profile Link
                            </>
                          )}
                        </Button>
                        
                        {/* Form Responses button hidden as per requirement */}
                        {/* <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/ai/actors/${actor.id}/form-submissions`)}
                          className="w-full mt-2 text-purple-600 border-purple-200 hover:bg-purple-50 relative"
                        >
                          <FileText className="w-3 h-3 mr-2" />
                          Form Responses
                          {unreadCounts[actor.id] > 0 && (
                            <Badge 
                              className="ml-2 bg-red-500 text-white h-5 min-w-5 px-1.5"
                              variant="destructive"
                            >
                              {unreadCounts[actor.id]}
                            </Badge>
                          )}
                        </Button> */}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Setup Status Dialog */}
      <Dialog open={!!warningsActor} onOpenChange={(open) => !open && setWarningsActor(null)}>
        <DialogContent className="max-w-lg" data-testid="actor-warnings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Setup status{warningsActor ? ` — ${warningsActor.name}` : ""}
            </DialogTitle>
            <DialogDescription>
              This actor was created successfully. Review the pending items below to get the most out of it.
            </DialogDescription>
          </DialogHeader>

          {warningsActor?.setup_status?.warnings?.length > 0 && (
            <div className="mt-2" data-testid="actor-warnings-section">
              <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm">
                <AlertTriangle className="w-4 h-4" />
                Recommended (chat won't work well without these)
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">{warningsActor.setup_status.warnings.length}</Badge>
              </div>
              <ul className="space-y-2 mt-2 pl-1">
                {warningsActor.setup_status.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`actor-warning-item-${i}`}>
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warningsActor?.setup_status?.optional?.length > 0 && (
            <div className="mt-4" data-testid="actor-optional-section">
              <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm">
                <Info className="w-4 h-4" />
                Optional (good to have)
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">{warningsActor.setup_status.optional.length}</Badge>
              </div>
              <ul className="space-y-2 mt-2 pl-1">
                {warningsActor.setup_status.optional.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`actor-optional-item-${i}`}>
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setWarningsActor(null)} data-testid="actor-warnings-close-btn">
              Close
            </Button>
            {warningsActor && (
              <Button
                onClick={() => { const a = warningsActor; setWarningsActor(null); handleEdit(a); }}
                data-testid="actor-warnings-edit-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Complete setup
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
